import React, { useEffect, useState } from 'react';
import {
  BetfairConnection,
  getBetfairConnection,
  connectBetfair,
  disconnectBetfair,
  betfairErrorMessage,
} from '../api/betfair';
import { EyeIcon, EyeOffIcon } from './PasswordToggleIcons';
import { useBetSlip } from '../context/BetSlipContext';
import './BetfairConnect.css';

const BetfairConnect: React.FC = () => {
  // Shared bet-slip status drives the "Betfair connected" indicators on the
  // races view; refresh it after connect/disconnect so they update without a
  // full page reload.
  const { refreshStatus } = useBetSlip();
  const [conn, setConn] = useState<BetfairConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [appKey, setAppKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setConn(await getBetfairConnection());
    } catch (err) {
      setError(betfairErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await connectBetfair(username.trim(), password, appKey.trim());
      setPassword('');
      setAppKey('');
      setNotice(
        `Connected as ${result.username} — balance $${result.account.balance?.toFixed(2)} ${result.account.currency}.`
      );
      await load();
      await refreshStatus();
    } catch (err) {
      setError(betfairErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setNotice(null);
    setDisconnecting(true);
    try {
      await disconnectBetfair();
      setUsername('');
      setConfirmingDisconnect(false);
      await load();
      await refreshStatus();
      setNotice('Your Betfair account has been disconnected.');
    } catch (err) {
      setError(betfairErrorMessage(err));
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="bfc-page">
      <h1>Betfair account</h1>
      <p className="bfc-intro">
        Connect your own Betfair account to place bets and see your balance. Your credentials are
        verified with Betfair and stored encrypted. Provide your own Betfair{' '}
        <strong>application key</strong> — a Betfair app key only works with the account that created
        it. Get yours from Betfair's Developer Program (Accounts API / "API app keys").
      </p>

      {error && <div className="bfc-error">{error}</div>}
      {notice && <div className="bfc-notice">{notice}</div>}

      {loading ? (
        <div className="bfc-loading">Loading…</div>
      ) : conn?.connected ? (
        <div className="bfc-card bfc-card--connected">
          <div className="bfc-status-row">
            <span className="bfc-dot bfc-dot--on" />
            <div>
              <div className="bfc-status-title">Connected</div>
              <div className="bfc-status-sub">
                {conn.username}
                {conn.connected_at && (
                  <> · since {new Date(conn.connected_at).toLocaleDateString('en-AU')}</>
                )}
              </div>
            </div>
          </div>
          {confirmingDisconnect ? (
            <div className="bfc-confirm">
              <span className="bfc-confirm-text">
                Disconnect your Betfair account? Your stored credentials will be removed.
              </span>
              <div className="bfc-confirm-actions">
                <button
                  className="bfc-disconnect"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
                </button>
                <button
                  className="bfc-confirm-cancel"
                  onClick={() => setConfirmingDisconnect(false)}
                  disabled={disconnecting}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button className="bfc-disconnect" onClick={() => setConfirmingDisconnect(true)}>
              Disconnect
            </button>
          )}
        </div>
      ) : (
        <form className="bfc-card" onSubmit={handleConnect}>
          <label className="bfc-field">
            <span>Betfair username</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              placeholder="your Betfair login"
              required
            />
          </label>
          <label className="bfc-field">
            <span>Betfair password</span>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </label>
          <label className="bfc-field">
            <span>Betfair application key</span>
            <input
              type="text"
              value={appKey}
              onChange={(e) => setAppKey(e.target.value)}
              autoComplete="off"
              placeholder="your Betfair app key"
              required
            />
          </label>
          <button className="bfc-connect" type="submit" disabled={submitting}>
            {submitting ? 'Connecting…' : 'Connect account'}
          </button>
        </form>
      )}
    </div>
  );
};

export default BetfairConnect;
